import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Phone, label: "Call us", value: "(876) 355-5819", href: "tel:+18763555819", testid: "contact-phone" },
  { icon: Phone, label: "Landline", value: "(876) 903-3276", href: "tel:+18769033276", testid: "contact-landline" },
  { icon: Mail, label: "Email", value: "clearlybec@gmail.com", href: "mailto:clearlybec@gmail.com", testid: "contact-email" },
  { icon: MapPin, label: "Visit us", value: "Shop #16 South Parade Plaza, Linstead, St Catherine, Jamaica", href: null, testid: "contact-address" },
  { icon: Clock, label: "Hours", value: "Mon – Sat · 9:00am – 5:30pm", href: null, testid: "contact-hours" },
];

const inputCls =
  "w-full bg-[#1C0D2C] border border-white/10 rounded-xl px-4 py-3 text-sm text-[#F8F7FA] placeholder:text-[#A29DB0]/60 focus:outline-none focus:ring-2 focus:ring-[#FF8A00]/60 focus:border-[#FF8A00]/40 transition-[box-shadow,border-color] duration-200";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Couldn't send your message. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 sm:py-36 border-t border-white/10 bg-[#1C0D2C]/30">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#FF8A00] mb-4">
            Contact us
          </p>
          <h2 className="font-display font-800 tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-8">
            Talk to a real person in Linstead
          </h2>
          <p className="text-[#A29DB0] text-base leading-relaxed max-w-md mb-12">
            Questions about a package, rates or setting up your mailbox? Reach out —
            we're at the plaza Monday to Saturday. We deliver in and around
            Spanish Town, St Catherine.
          </p>

          <div className="space-y-6">
            {INFO.map((item) => {
              const inner = (
                <>
                  <span className="w-11 h-11 rounded-full border border-white/10 bg-[#1C0D2C] flex items-center justify-center shrink-0">
                    <item.icon className="w-4.5 h-4.5 w-5 h-5 text-[#FF8A00]" />
                  </span>
                  <span>
                    <span className="block font-mono-accent text-xs tracking-widest uppercase text-[#A29DB0]">
                      {item.label}
                    </span>
                    <span className="block mt-1 text-sm sm:text-base">{item.value}</span>
                  </span>
                </>
              );
              return item.href ? (
                <a
                  key={item.testid}
                  data-testid={item.testid}
                  href={item.href}
                  className="flex items-center gap-4 group hover:opacity-80 transition-opacity duration-200"
                >
                  {inner}
                </a>
              ) : (
                <div key={item.testid} data-testid={item.testid} className="flex items-center gap-4">
                  {inner}
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="rounded-2xl border border-white/10 bg-[#1C0D2C]/70 backdrop-blur-xl inner-glow p-8 sm:p-10 space-y-5 self-start"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="contact-name" className="block font-mono-accent text-xs tracking-widest uppercase text-[#A29DB0] mb-2">
                Name
              </label>
              <input
                id="contact-name"
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-phone" className="block font-mono-accent text-xs tracking-widest uppercase text-[#A29DB0] mb-2">
                Phone <span className="normal-case tracking-normal">(optional)</span>
              </label>
              <input
                id="contact-phone"
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="(876) 000-0000"
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label htmlFor="contact-email" className="block font-mono-accent text-xs tracking-widest uppercase text-[#A29DB0] mb-2">
              Email
            </label>
            <input
              id="contact-email"
              data-testid="contact-email-input"
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="block font-mono-accent text-xs tracking-widest uppercase text-[#A29DB0] mb-2">
              Message
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-message-input"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="How can we help?"
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            data-testid="contact-submit-btn"
            type="submit"
            disabled={sending}
            className="w-full py-3.5 rounded-full bg-[#FF8A00] text-[#12071F] font-semibold hover:bg-[#FF9F2E] disabled:opacity-60 transition-colors duration-200 flex items-center justify-center gap-2"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {sending ? "Sending…" : "Send Message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
